import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatRippleModule } from '@angular/material/core';
import {
  BreakpointObserver,
  Breakpoints,
  LayoutModule,
} from '@angular/cdk/layout';
import {
  Router,
  RouterLink,
  RouterOutlet,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { Observable, Subject, takeUntil, of } from 'rxjs';
import { LoadingIndicatorComponent } from './components/loading-indicator/loading-indicator.component';
import { DualAuthService } from './services/DualAuthService';
import { RoleService } from './services/RoleService';
import { NavigationService, NavigationItem } from './services/NavigationService';
import { environment } from '../environments/environment';
import { AuthUser, EmployeeRole, Permission, UserType } from './models/AuthUser';
import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { LoadingService } from './services/LoadingService';
import { FooterComponent } from './components/footer/footer.component';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SignalRService } from './services/SignalRService';
import { ThemeService } from './services/ThemeService';
import { Store } from '@ngrx/store';
import { OrderSearchComponent } from './components/order-search/order-search.component';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet,
    MatSidenavModule,
    FormsModule,
    RouterLink,
    MatDividerModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatRippleModule,
    MatMenuModule,
    MatBadgeModule,
    MatTooltipModule,
    LoadingIndicatorComponent,
    LayoutModule,
    FooterComponent,
    MatSnackBarModule,
    OrderSearchComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  standalone: true,
  animations: [
    trigger('routeAnimations', [
      transition('* <=> *', [
        // Set up both pages for cross-fade
        query(':enter, :leave', [
          style({
            position: 'absolute',
            width: '100%',
            top: 0,
            left: 0
          })
        ], { optional: true }),
        // Cross-fade: simultaneous fade out/in with proper layering
        group([
          query(':leave', [
            style({ opacity: 1, zIndex: 1 }),
            animate('250ms ease-out', style({ opacity: 0 }))
          ], { optional: true }),
          query(':enter', [
            style({ opacity: 0, zIndex: 2 }),
            animate('250ms ease-in', style({ opacity: 1 }))
          ], { optional: true })
        ])
      ]),
    ]),
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('200ms ease', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1 }),
        animate('150ms ease', style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() opened: boolean = false;
  isLoggedIn$: Observable<boolean>;
  sidenavMode: 'side' | 'over' = 'over';

  // Navigation and user state
  userTypeEnum = UserType;
  isAuthenticated = false;
  currentUser: AuthUser | null = null;
  navigationItems: NavigationItem[] = [];
  expandedItems: Set<string> = new Set(['admin']); // Default expanded items
  // Hide user info sidebar widget in production builds
  showUserStatus: boolean = !environment.production;

  constructor(
    public router: Router,
    private breakpointObserver: BreakpointObserver,
    private dualAuthService: DualAuthService,
    private roleService: RoleService,
    private navigationService: NavigationService,
    private loading: LoadingService,
    private snackBar: MatSnackBar,
    private signalR: SignalRService,
    private themeService: ThemeService,
    private store: Store
  ) {
    this.isLoggedIn$ = this.dualAuthService.isAuthenticated$;
    // Initialize dark mode from ThemeService
    this.darkMode$ = this.themeService.darkMode$;
  }

  ngOnInit(): void {
    // Subscribe to authentication state
    this.dualAuthService.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAuth) => {
        this.isAuthenticated = isAuth;
        this.updateNavigation();
      });

    // Subscribe to current user
    this.dualAuthService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.currentUser = user;
        this.updateNavigation();
      });

    // Handle responsive sidebar
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result.matches) {
          // Mobile: use over mode and close sidebar
          this.sidenavMode = 'over';
          this.opened = false;
        } else {
          // Desktop: use side mode and keep it open by default
          this.sidenavMode = 'side';
          this.opened = true;
        }
      });

    // Route loading indicator
    // Note: We trigger start('route') on manual nav clicks for immediate feedback
    // NavigationStart handles browser back/forward buttons and direct URL navigation
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        // Only start loading if not already triggered by click
        // Check if this is a browser navigation (back/forward/direct URL)
        if (!this.loading.isRouteLoading()) {
          this.loading.start('route');
        }
      }
      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loading.end('route');
      }
    });

    if (this.isAuthenticated) this.signalR.startConnection();
    else this.signalR.stopConnection();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateNavigation(): void {
    if (this.isAuthenticated && this.currentUser && this.currentUser.userType === UserType.EMPLOYEE) {
      // Use app roles only (from 'roles' claim)
      const appRoles = (this.currentUser.roles || []) as string[];
      const groupNames = (this.currentUser.groups || []) as string[];
      const rolesFromApp = this.roleService.mapAppRolesToRoles(appRoles);
      const rolesFromGroups = this.roleService.mapGroupsToRoles(groupNames);
      const userRoles = Array.from(new Set([...(rolesFromApp || []), ...(rolesFromGroups || [])]));

      // Get permissions for roles
      const userPermissions =
        this.roleService.getPermissionsForRoles(userRoles);

      // Dev log: detected roles/permissions for employee
      try {
        // eslint-disable-next-line no-console
        console.log('[Auth] Employee roles:', userRoles, 'permissions:', userPermissions);
      } catch {}

      // Update user object with permissions only; keep original roles (raw app-role values)
      this.currentUser = {
        ...this.currentUser,
        permissions: userPermissions
      };

      // Administrator role sees all navigation
      if (userRoles.includes(EmployeeRole.ADMINISTRATOR)) {
        this.navigationItems = this.navigationService.getNavigationForUser(Object.values(Permission));
      } else {
        // Get filtered navigation based on permissions
        this.navigationItems = this.navigationService.getNavigationForUser(userPermissions);
      }
    } else if (this.isAuthenticated && this.currentUser && this.currentUser.userType === UserType.CUSTOMER) {
      // Customer navigation (simplified)
      this.navigationItems = [
        {
          label: 'Dashboard',
          route: '',
          icon: 'dashboard',
          requiredPermissions: [],
        },
        {
          label: 'Meine Aufträge',
          route: 'orders',
          icon: 'shopping_cart',
          requiredPermissions: [],
        },
        {
          label: 'Mitarbeiter',
          route: 'manage-employees',
          icon: 'people',
          requiredPermissions: [],
        }
      ];
      // Dev log: detected customer session
      try {
        // eslint-disable-next-line no-console
        console.log('[Auth] Customer session active');
      } catch {}
    } else {
      // Not authenticated - no navigation items
      this.navigationItems = [];
    }
  }

  toggleOpen = () => {
    this.opened = !this.opened;
  };

  isActiveRoute(route: string): boolean {
    return (
      this.router.url === `/${route}` );
  }

  isExpanded(route: string): boolean {
    return this.expandedItems.has(route);
  }

  toggleExpanded(route: string): void {
    if (this.expandedItems.has(route)) {
      this.expandedItems.delete(route);
    } else {
      this.expandedItems.add(route);
    }
  }

  onNavItemClick(item: NavigationItem): void {
    // Close any open dropdown
    this.hoveredItem = null;
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    if (item.children && item.children.length) {
      this.toggleExpanded(item.route);
      return;
    }
    if (item.externalUrl) {
      const url = this.buildExternalUrl(item.externalUrl);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      try { if (win) win.opener = null; } catch {}
      return;
    }

    // Check if we're already on this route
    const targetUrl = '/' + item.route;
    if (this.router.url === targetUrl) {
      // Already on this page, don't show loading indicator
      return;
    }

    // Show loading indicator immediately on click
    this.loading.start('route');
    this.router.navigate([item.route]);
  }

  onChildNavClick(item: NavigationItem): void {
    // Close dropdown when clicking on child item
    this.hoveredItem = null;
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    if (item.externalUrl) {
      const url = this.buildExternalUrl(item.externalUrl);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      try { if (win) win.opener = null; } catch {}
    } else {
      // Check if we're already on this route
      const targetUrl = '/' + item.route;
      if (this.router.url === targetUrl) {
        // Already on this page, don't show loading indicator
        return;
      }

      // Show loading indicator immediately on click
      this.loading.start('route');
      this.router.navigate([item.route]);
    }
  }

  private buildExternalUrl(path: string): string {
    const base = environment.backend.baseUrl.replace(/\/$/, '');
    const p = path.startsWith('/') ? path : '/' + path;
    return base + p;
  }

  getUserDisplayName(): string {
    if (!this.currentUser) return 'Guest';

    if (this.currentUser.firstName && this.currentUser.lastName) {
      return `${this.currentUser.firstName} ${this.currentUser.lastName}`;
    } else if (this.currentUser.firstName) {
      return this.currentUser.firstName;
    } else if (this.currentUser.email) {
      return this.currentUser.email.split('@')[0];
    }
    return 'User';
  }

  // New methods for Apple-style layout
  isScrolled = false;
  hoveredItem: NavigationItem | null = null;
  darkMode$: Observable<boolean>;
  notifications: Observable<any[]> = of([]);
  unreadCount = 0;
  private hoverTimeout: any = null;

  onNavHover(item: NavigationItem): void {
    // Clear any pending close timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    // If hovering over a different item, close any open dropdown immediately
    if (this.hoveredItem && this.hoveredItem !== item) {
      this.hoveredItem = null;
    }

    // Open dropdown if this item has children
    if (item.children && item.children.length > 0) {
      this.hoveredItem = item;
    } else {
      // If hovering over item without children, close any dropdown
      this.hoveredItem = null;
    }
  }

  onDropdownEnter(item: NavigationItem): void {
    // Clear any pending close timeout when entering dropdown
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
    // Keep the dropdown open
    this.hoveredItem = item;
  }

  onNavLeave(item: NavigationItem): void {
    // Add a small delay before closing to allow mouse to move to dropdown
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }

    this.hoverTimeout = setTimeout(() => {
      // Only close if we're still hovering over the same item
      if (this.hoveredItem === item) {
        this.hoveredItem = null;
      }
      this.hoverTimeout = null;
    }, 100);
  }

  toggleDarkMode(): void {
    this.themeService.toggleDarkMode();
  }

  getInitials(): string {
    if (!this.currentUser) return '?';

    if (this.currentUser.firstName && this.currentUser.lastName) {
      return `${this.currentUser.firstName[0]}${this.currentUser.lastName[0]}`.toUpperCase();
    } else if (this.currentUser.firstName) {
      return this.currentUser.firstName[0].toUpperCase();
    } else if (this.currentUser.email) {
      return this.currentUser.email[0].toUpperCase();
    }
    return 'U';
  }

  getCompactName(): string {
    if (!this.currentUser) return 'Guest';

    if (this.currentUser.firstName) {
      return this.currentUser.firstName;
    } else if (this.currentUser.email) {
      return this.currentUser.email.split('@')[0];
    }
    return 'User';
  }

  getUserTypeDisplay(): string {
    if (!this.currentUser) return '';
    return this.currentUser.userType === UserType.EMPLOYEE ? 'Mitarbeiter' : 'Kunde';
  }

  login(): void {
    this.router.navigate(['/login']);
  }

  logout(): void {
    this.dualAuthService.logout();
    this.router.navigate(['/']);
  }

  /*navigateToProfile(): void {
    this.router.navigate(['/whoami']);
  }*/
   navigateToProfile(): void {
    if (!this.currentUser) { this.router.navigate(['/']); return; }
    if (this.currentUser.userType === this.userTypeEnum.EMPLOYEE) {
      this.router.navigate(['/whoami/employee']);
    } else if (this.currentUser.userType === this.userTypeEnum.CUSTOMER) {
      this.router.navigate(['/whoami/customer']);
    }
    else {
      this.router.navigate(['/']);
    }
  }

  navigateToSettings(): void {
    this.router.navigate(['/settings']);
  }

  navigateToHelp(): void {
    this.router.navigate(['/faq']);
  }

  markNotificationsRead(notification: any): void {
    // Implement notification read logic
    console.log('Mark notification as read:', notification);
  }

  isEmployee(): boolean {
    return this.currentUser?.userType === UserType.EMPLOYEE;
  }
}
